import React, { useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import Color from 'color';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Avatar from '../../../../shared/_TBComponents/Avatar/Avatar';
import useWindowSize from '../../../../shared/hooks/useWindowSize';

import { deleteTopPrWidget, fetchTopPr } from '../../ducks/feedActions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const CloseWidgetDiv = styled('div')`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  width: 12px;
  font-weight: 500;
  border: 0px;
  cursor: pointer !important;
`;

const Container = styled('div')`
  width: 100%;
  display: flex;
  padding: 10px 0;
  padding-bottom: ${(props) => (props.errorMessage ? '10px' : '')};
  align-items: center;
  @media screen and (max-width: 1200px) {
    height: auto;
    padding: ${(props) => (props.userName || props.errorMessage ? '20px 0' : '20px 0 0 0')};
    justify-content: center;
  }
`;

const DisplayValueText = styled('div')`
  font-size: ${(props) => (props.numLen && props.numLen > 6 ? '72px' : '80px')};
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 400;
  margin: 0;
  line-height: 80px;
  padding: ${(props) => (props.displayValue ? '10px 0 0 0' : '0')};
  color: #ffffff;
`;

const ExerciseLabel = styled('div')`
  display: block;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 20px;
  line-height: 20px;
  font-weight: 900;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-top: 5px;
  margin-bottom: 10px;
`;

const FlexibleText = styled('div')`
  font-family: 'Nunito Sans', sans-serif;
  font-weight: 400;
  color: #ffffff;
  font-size: ${(props) => props.fontSize || '16px'};
  line-height: ${(props) => props.lineHeight || '16px'};
  margin-bottom: ${(props) => props.marginBottom || ''};
`;

const LiftIconWrapper = styled('span')`
  height: 100%;
  width: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media screen and (max-width: 1200px) {
    height: 80px;
  }
`;

const Widget = styled('li')`
  position: relative;
  width: 420px;
  margin: 0 auto 30px auto;
  background: ${(props) => `linear-gradient(134deg, ${props.colorAccent} -8%, ${props.darkerColorAccent} 200%)`};
  color: #ffffff;
  border-radius: 10px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  @media screen and (max-width: 1200px) {
    width: 250px;
  }
`;

const WidgetSelectionContainer = styled('div')`
  width: 400px;
  height: ${(props) => (props.errorMessage ? '240px' : '220px')};
  margin: 0 auto;
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  @media screen and (max-width: 1200px) {
    width: 180px;
    height: auto;
    text-align: center;
  }
`;

const WidgetTextDiv = styled('div')`
  height: 100%;
  width: 310px;
  padding-left: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media screen and (max-width: 1200px) {
    margin-top: 16px;
    padding-left: 0;
  }
`;

const TopPrWidget = ({ topPrObject }) => {
  const windowWidth = useWindowSize().width;
  const theme = useTheme();

  const dispatch = useDispatch();
  const selectedGroups = useSelector((state) => state.feed.data.selectedGroups);

  const {
    data, exerciseName, values, errorMessage,
  } = topPrObject;
  const { id, gender } = values;
  const { userName, userPic, displayValue } = data.length > 0 && data[0];
  const numLen = displayValue && displayValue.length;

  // Use refs to store latest values for the interval callback
  const valuesRef = useRef(values);
  const selectedGroupsRef = useRef(selectedGroups);

  const themeColors = useMemo(() => {
    const themeColor = theme.colorAccent ? theme.colorAccent : '#FFFFFF';
    const colorAccent = Color(themeColor);
    const darkerColorAccent = colorAccent.darken(0.5);
    return { colorAccent, darkerColorAccent };
  }, [theme.colorAccent]);

  const displayedExerciseName = useMemo(() => {
    const nameLimit = windowWidth < 1200 ? 30 : 40;
    return exerciseName.length > nameLimit
      ? exerciseName.slice(0, nameLimit - 3).concat('...')
      : exerciseName;
  }, [exerciseName, windowWidth]);

  const displayedGender = useMemo(() => {
    if (gender === 0) return 'All';
    if (gender === 1) return 'Male';
    if (gender === 2) return 'Female';
    return 'Undefined';
  }, [gender]);

  const displayedName = useMemo(() => {
    if (userName) {
      return userName.length < 20 ? userName : `${userName.slice(0, 17)}...`;
    }
    return 'No Leader';
  }, [userName]);

  const modifiedErrorMessage = useMemo(
    () => (errorMessage === 'No leaderboards found based on selected groups'
      ? 'Leaderboard tracking is not enabled for current group selection'
      : errorMessage),
    [errorMessage],
  );

  const handleDeleteWidget = () => {
    dispatch(deleteTopPrWidget(id));
    feedTracker('TV Mode', 'Top performance widget removed');
  };

  // Keep refs in sync with current values
  useEffect(() => {
    valuesRef.current = values;
    selectedGroupsRef.current = selectedGroups;
  }, [values, selectedGroups]);

  useEffect(() => {
    const copiedValues = {
      ...values,
      groups: selectedGroups,
    };
    dispatch(fetchTopPr(copiedValues, 'edit'));
  }, [JSON.stringify(selectedGroups)]);

  useEffect(() => {
    // update widget data to account for new workout entries every 30 seconds
    const interval = setInterval(() => {
      const updatedValues = {
        ...valuesRef?.current,
        groups: selectedGroupsRef?.current,
      };
      dispatch(fetchTopPr(updatedValues, 'edit'));
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const renderAvatarSection = () => (
    <LiftIconWrapper>
      {userName && userPic && <Avatar avatar={userPic || ''} size={80} />}
      {(!userName || !userPic) && (
        <IcomoonReact
          iconSet={iconSet}
          size={80}
          icon='trophy'
          color='#ffffff'
        />
      )}
    </LiftIconWrapper>
  );

  const renderTextContent = () => (
    <WidgetTextDiv numLen={numLen} displayValue={displayValue}>
      <ExerciseLabel>{displayedExerciseName}</ExerciseLabel>
      {gender !== 0 && (
        <FlexibleText fontSize='16px' lineHeight='16px' marginBottom='10px'>
          (
          {displayedGender}
          )
        </FlexibleText>
      )}
      <FlexibleText fontSize='24px' lineHeight='32px'>
        {displayedName}
      </FlexibleText>
      <DisplayValueText numLen={numLen} displayValue={displayValue}>
        {displayValue || '-'}
      </DisplayValueText>
      {errorMessage && (
        <FlexibleText fontSize='16px' lineHeight='16px'>
          {modifiedErrorMessage}
        </FlexibleText>
      )}
    </WidgetTextDiv>
  );

  return (
    <Widget
      colorAccent={themeColors.colorAccent}
      darkerColorAccent={themeColors.darkerColorAccent}
    >
      <CloseWidgetDiv onClick={handleDeleteWidget}>
        <IcomoonReact
          iconSet={iconSet}
          size={12}
          icon='remove'
          color='#ffffff'
        />
      </CloseWidgetDiv>
      <Container userName={userName} errorMessage={errorMessage}>
        <WidgetSelectionContainer errorMessage={errorMessage}>
          {renderAvatarSection()}
          {renderTextContent()}
        </WidgetSelectionContainer>
      </Container>
    </Widget>
  );
};

TopPrWidget.propTypes = {
  topPrObject: PropTypes.instanceOf(Object).isRequired,
};

export default TopPrWidget;
